import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerSection,
  DrawerTrigger,
} from "~/components/reusable/Drawer"
import {
  calculateIncomingStockStatus,
  type IncomingOrderDetail,
  skuCostShareInOrder,
  skuQuantityInOrder,
} from "~/utils/incomingOrdersUtils"
import { useMemo, useRef, useState } from "react"
import { uniq } from "~/utils/tsUtils"
import { sumBy } from "lodash"
import { Badge } from "~/components/reusable/Badge"
import { formatNumber, formatUniqueValues } from "~/utils/formatting"
import { api } from "~/utils/api"
import { useVariantsByIds } from "~/hooks/useVariants"
import InformationGrid from "~/components/reusable/InformationGrid"
import InformationRow from "~/components/reusable/InformationRow"
import { IncomingOrderSourceBadge } from "~/components/purchaseOrder/incomingOrders/IncomingOrderSourceBadge"
import {
  Boat,
  CalendarBlank,
  CalendarDot,
  CalendarDots,
  CaretLeft,
  CaretRight,
  CircleDashed,
  CurrencyCircleDollar,
  Factory,
  Fingerprint,
  Tag,
  Truck,
  TShirt,
  Warehouse,
} from "@phosphor-icons/react"
import { formatDateSpan } from "~/utils/datetime"
import { IncomingOrdersStatusBadge } from "~/components/purchaseOrder/incomingOrders/IncomingOrdersStatusBadge"
import ShipmentMethodIcon from "~/components/icons/ShipmentMethodIcon"
import { formatVariantId } from "~/utils/helpers"
import { type IDetailCellRendererParams, type ColDef } from "ag-grid-community"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { type AgGridReact } from "ag-grid-react"
import { startCase, lowerCase } from "lodash"
import {
  SkuCellRenderer,
  VariantCellRenderer,
} from "~/components/planning/ProductTable/ProductTableV2"
import { openProduct } from "~/utils/routerUtils"
import { useRouter } from "@tanstack/react-router"
import clsx from "clsx"
import { sortSizesComparator } from "~/utils/sizeSplit"
import { createCustomColumn, createStringColumn, createSumColumn } from "~/utils/columnTemplates"
import { useGetCollections } from "~/hooks/useCollections"
import { useExtraDataDisplayNameMap, useIncomingOrderExtraDataKeys } from "~/hooks/useSku"
import { getExtraDataDisplayName, ExtraDataTable } from "~/utils/dynamicTableUtils"
import { UniqueValueListContent } from "~/components/reusable/table/tooltips/UniqueListTooltip"
import { ERP_ETD_TOOLTIP } from "~/constants/incomingOrders"

interface IncomingOrdersDrawerProps {
  trigger: React.ReactNode
  incomingOrders: IncomingOrderDetail[]
  customOpen?: boolean
  onClose?: () => void
  skuId?: string
}

type IncomingOrderDrawerRow = IncomingOrderDetail & {
  id: string
  variantName?: string
  productName?: string
  imageUrl?: string
  hasDetailData?: boolean
}

const IncomingOrdersDrawer = ({
  trigger,
  incomingOrders,
  customOpen,
  onClose,
  skuId,
}: IncomingOrdersDrawerProps) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = customOpen !== undefined
  const open = isControlled ? customOpen : internalOpen

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setInternalOpen(next)
    if (!next) onClose?.()
  }

  if (incomingOrders.length === 0) return null

  const multipleWarehouses = new Set(incomingOrders.map((order) => order.warehouseId)).size > 1

  return (
    <Drawer direction="right" open={open} onOpenChange={handleOpenChange} handleOnly>
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent className={clsx("min-w-[500px]", multipleWarehouses && "min-w-[630px]")}>
        {open && <IncomingOrdersDrawerBody incomingOrders={incomingOrders} skuId={skuId} />}
      </DrawerContent>
    </Drawer>
  )
}

const IncomingOrdersDrawerBody = ({
  incomingOrders: rawIncomingOrders,
  skuId,
}: {
  incomingOrders: IncomingOrderDetail[]
  skuId?: string
}) => {
  const router = useRouter()
  const gridRef = useRef<AgGridReact<IncomingOrderDrawerRow> | null>(null)
  const { data: company } = api.company.getCompany.useQuery()
  const incomingOrders = useMemo(
    () =>
      skuId
        ? rawIncomingOrders.map((o) => ({
            ...o,
            quantity: skuQuantityInOrder(o, skuId),
            cost: skuCostShareInOrder(o, skuId),
          }))
        : rawIncomingOrders,
    [rawIncomingOrders, skuId],
  )
  const variantIds = useMemo(() => {
    return uniq(incomingOrders.map((order) => order.variantId))
  }, [incomingOrders])
  const variants = useVariantsByIds(variantIds)

  const { data: warehouses } = api.warehouse.get.useQuery()
  const { data: hasShipmentMethod } = api.purchaseOrder.hasShipmentMethod.useQuery()
  const { data: hasExternalCollectionId } = api.purchaseOrder.hasExternalCollectionId.useQuery()
  const { data: hasVendorId } = api.purchaseOrder.hasVendorId.useQuery()
  const { data: hasExpectedDepartureDate } = api.purchaseOrder.hasExpectedDepartureDate.useQuery()
  const { data: vendors } = api.vendor.getAll.useQuery()
  const [showAllVariants, setShowAllVariants] = useState(false)
  const { data: collections } = useGetCollections()

  const utils = api.useUtils()
  const { data: displayNameMap } = useExtraDataDisplayNameMap()
  const { data: incomingOrderExtraDataKeys } = useIncomingOrderExtraDataKeys()

  const extraDataFields = useMemo(() => {
    if (!incomingOrderExtraDataKeys) return []
    return incomingOrderExtraDataKeys.map((key) => key.replace("(incoming-order-extradata)", ""))
  }, [incomingOrderExtraDataKeys])

  // Get aggregated extra data values for display (formatted string + unique values for tooltip)
  const extraDataValues = useMemo(() => {
    const valuesMap = new Map<string, Set<string>>()

    incomingOrders.forEach((order) => {
      if (order.extraData && typeof order.extraData === "object") {
        Object.entries(order.extraData).forEach(([key, value]) => {
          if (value != null) {
            if (!valuesMap.has(key)) {
              valuesMap.set(key, new Set())
            }
            valuesMap.get(key)!.add(String(value))
          }
        })
      }
    })

    const result = new Map<string, { formatted: string; uniqueValues: string[] }>()
    valuesMap.forEach((values, key) => {
      const uniqueValues = Array.from(values)
      result.set(key, {
        formatted:
          uniqueValues.length === 1 ? uniqueValues[0]! : formatUniqueValues(uniqueValues, "values"),
        uniqueValues,
      })
    })
    return result
  }, [incomingOrders])
  const collectionIdsInOrders = useMemo(() => {
    return uniq(
      incomingOrders
        .map((order) => order.externalCollectionId)
        .filter((id): id is string => Boolean(id)),
    )
  }, [incomingOrders])

  const quantity = useMemo(() => {
    return sumBy(incomingOrders, "quantity")
  }, [incomingOrders])
  const cost = useMemo(() => {
    return sumBy(incomingOrders, (order) => order.cost ?? 0)
  }, [incomingOrders])

  const expectedArrivalDateText = useMemo(
    () => formatDateSpan(incomingOrders.map((o) => o.expectedArrivalDate)),
    [incomingOrders],
  )
  const expectedDepartureDateText = useMemo(
    () => formatDateSpan(incomingOrders.map((o) => o.expectedDepartureDate)),
    [incomingOrders],
  )
  const orderDateText = useMemo(
    () => formatDateSpan(incomingOrders.map((o) => o.orderDate)),
    [incomingOrders],
  )

  const statuses = useMemo(() => {
    return uniq(incomingOrders.map((order) => calculateIncomingStockStatus(order)))
  }, [incomingOrders])

  const productionOrderIds = useMemo(() => {
    return uniq(incomingOrders.map((order) => order.productionOrderId))
  }, [incomingOrders])

  const finalIncomingOrders: IncomingOrderDrawerRow[] = useMemo(() => {
    // Multiple backend rows can share the same variant+warehouse+order when SKUs within
    // the variant have different arrival dates. Aggregate them into one row per variant
    // so the table shows one line per variant with the correct total quantity.
    const aggregated = new Map<string, IncomingOrderDrawerRow>()
    for (const order of incomingOrders) {
      const key = `${order.variantId}_${order.warehouseId}_${order.productionOrderId}`
      const existing = aggregated.get(key)
      if (existing) {
        aggregated.set(key, {
          ...existing,
          quantity: existing.quantity + order.quantity,
          cost: (existing.cost ?? 0) + (order.cost ?? 0),
        })
        continue
      }
      const variant = variants.find((v) => v.variantId === order.variantId)
      aggregated.set(key, {
        ...order,
        id: key,
        variantName: variant?.variantName ?? order.variantId,
        productName: variant?.productName ?? order.variantId,
        imageUrl: variant?.imageUrl ?? undefined,
        hasDetailData: Boolean(variant?.skuIds && variant.skuIds.length > 0),
      })
    }
    return Array.from(aggregated.values())
  }, [incomingOrders, variants])

  const multipleWarehouses = useMemo(() => {
    return new Set(incomingOrders.map((order) => order.warehouseId)).size > 1
  }, [incomingOrders])

  const columnDefs = useMemo((): ColDef<IncomingOrderDrawerRow>[] => {
    const warehouseColumn: ColDef<IncomingOrderDrawerRow> = createStringColumn({
      headerName: "Warehouse",
      field: "warehouseId",
      valueFormatter: (params) =>
        warehouses?.find((w) => w.id === params.value)?.name ?? params.value,
      minWidth: 130,
      filter: true,
    })

    const columns: ColDef<IncomingOrderDrawerRow>[] = [
      createCustomColumn({
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          innerRenderer: (params: any) => {
            const onOpen = (e: any) => {
              const newTab = e.metaKey || e.ctrlKey
              const variant = variants.find((v) => v.variantId === params.data?.variantId)
              if (!variant) return
              openProduct(variant.productId, [variant.variantId], router, newTab)
            }
            return <VariantCellRenderer {...params} aggregations={[]} onOpen={onOpen} />
          },
        },
        headerName: "Variant",
        field: "variantId",
        valueFormatter: (params) => formatVariantId(params.value),
        minWidth: 300,
      }),
      createSumColumn({
        headerName: "Quantity",
        field: "quantity",
        minWidth: 100,
        alignment: "left",
      }),
    ]

    if (multipleWarehouses) {
      columns.push(warehouseColumn)
    }

    if (collectionIdsInOrders.length > 1) {
      columns.push({
        headerName: "Collection",
        field: "externalCollectionId",
        minWidth: 150,
        valueFormatter: (params) =>
          collections?.find((x) => x.externalId == params.value)?.name ?? "-",
        valueGetter: (params) => params.data?.externalCollectionId ?? "No collection",
      })
    }

    return columns
  }, [warehouses, multipleWarehouses, variants, collectionIdsInOrders, router, collections])

  const defaultColumnDef = useMemo(() => {
    return {
      suppressHeaderMenuButton: true,
      suppressMovable: true,
    }
  }, [])

  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        defaultColDef: defaultColumnDef,
        columnDefs: [
          {
            headerName: "SKU",
            field: "skuId",
            pinned: "left",
            cellRenderer: SkuCellRenderer,
            sort: "asc",
            sortingOrder: ["asc", "desc"],
            // @ts-expect-error
            comparator: (valueA, valueB, nodeA, nodeB) => {
              const sizeA = nodeA.data?.size
              const sizeB = nodeB.data?.size
              if (!sizeA || !sizeB) return 0
              return sortSizesComparator(sizeA, sizeB)
            },
          },
          { field: "size", hide: true }, // Used in the SkuCellRenderer
          ...columnDefs.filter((col) => col.field !== "variantId"),
        ],
      },
      getDetailRowData: (params) => {
        const variant = params.data
        const variantData = variants.find((v) => v.variantId === variant.variantId)

        if (!variantData?.skuIds) {
          params.successCallback([])
          return
        }

        const detailSkuIds = skuId
          ? variantData.skuIds.filter((id) => id === skuId)
          : variantData.skuIds

        utils.sku.allIncomingOrders
          .fetch({ skuIds: detailSkuIds })
          .then((incomingOrdersMap) => {
            const skuData = detailSkuIds.map((skuId) => {
              const skuIncomingOrders = incomingOrdersMap?.get(skuId) || []
              const skuIncomingOrder = skuIncomingOrders.find(
                (order) =>
                  order.warehouseId === variant.warehouseId &&
                  order.productionOrderId === variant.productionOrderId,
              )

              return {
                skuId,
                size: variantData.skuData?.find((sku) => sku.skuId === skuId)?.size || "",
                quantity: skuIncomingOrder?.quantity || 0,
                warehouseId: variant.warehouseId,
                expectedArrivalDate:
                  skuIncomingOrder?.expectedArrivalDate || variant.expectedArrivalDate,
                orderDate: skuIncomingOrder?.orderDate || variant.orderDate,
                productionOrderId: skuIncomingOrder?.productionOrderId || variant.productionOrderId,
                shipmentMethod: skuIncomingOrder?.shipmentMethod || variant.shipmentMethod,
              }
            })

            params.successCallback(skuData)
          })
          .catch((error) => {
            console.error("Failed to fetch incoming orders:", error)
            params.successCallback([])
          })
      },
    } as IDetailCellRendererParams<IncomingOrderDrawerRow, any>
  }, [defaultColumnDef, columnDefs, variants, utils, skuId])

  const variantsTable = useMemo(() => {
    return (
      <DataTableBaseV2
        id="incoming-orders-drawer"
        gridRef={gridRef}
        rowData={finalIncomingOrders}
        columnDefs={columnDefs}
        defaultColumnDef={defaultColumnDef}
        rowIdField="id"
        masterDetail={true}
        detailCellRendererParams={detailCellRendererParams}
        minHeight={100}
      />
    )
  }, [finalIncomingOrders, columnDefs, defaultColumnDef, gridRef, detailCellRendererParams])

  return (
    <>
      <DrawerHeader className="flex flex-col gap-2">
        <div className="label-base">
          {productionOrderIds.length > 1
            ? `${productionOrderIds.length} orders`
            : `${productionOrderIds[0]}`}
        </div>
        <div className="flex flex-row items-center gap-2 flex-wrap">
          <Badge variant="neutral" size="sm">
            {formatNumber(quantity)} units
          </Badge>
          <Badge variant="neutral" size="sm">
            {formatNumber(cost)} {company?.currency}
          </Badge>
          {uniq(
            incomingOrders.map((o) => o.source).filter((s): s is "Hakio" | "ERP" => s != null),
          ).map((source) => (
            <IncomingOrderSourceBadge key={source} source={source} />
          ))}
        </div>
      </DrawerHeader>
      <DrawerBody>
        {!showAllVariants && (
          <>
            <DrawerSection className="flex flex-col gap-3 select-text pt-0">
              <div className="label-base">Order Details</div>
              <InformationGrid>
                <InformationRow
                  title="Production Order ID"
                  value={formatUniqueValues(
                    uniq(incomingOrders.map((order) => order.productionOrderId)),
                    "IDs",
                  )}
                  titleIcon={<Fingerprint />}
                  noTruncate
                />
                <InformationRow
                  title="Status"
                  titleIcon={<CircleDashed />}
                  valueComponent={
                    statuses.length === 1 ? (
                      <IncomingOrdersStatusBadge
                        status={calculateIncomingStockStatus(incomingOrders[0]!)}
                        size="sm"
                      />
                    ) : undefined
                  }
                  value={statuses.length > 1 ? formatUniqueValues(statuses, "statuses") : undefined}
                />

                <InformationRow
                  title="Warehouse"
                  titleIcon={<Warehouse />}
                  noTruncate
                  value={formatUniqueValues(
                    uniq(
                      incomingOrders
                        .map(
                          (order) =>
                            warehouses?.find((w) => w.id === order.warehouseId)?.name ||
                            order.warehouseId,
                        )
                        .filter(Boolean),
                    ),
                    "warehouses",
                  )}
                />
                {hasExpectedDepartureDate && (
                  <InformationRow
                    title="ETD"
                    titleIcon={<CalendarDots />}
                    tooltip={ERP_ETD_TOOLTIP}
                    value={expectedDepartureDateText}
                    noTruncate
                  />
                )}
                <InformationRow
                  title="Expected Arrival Date"
                  titleIcon={<CalendarDot />}
                  value={expectedArrivalDateText}
                  noTruncate
                />
                <InformationRow
                  title="Order Placed Date"
                  titleIcon={<CalendarBlank />}
                  value={orderDateText}
                  noTruncate
                />
                <InformationRow
                  title="Quantity"
                  titleIcon={<Truck />}
                  value={formatNumber(quantity)}
                  noTruncate
                />
                <InformationRow
                  title="Cost"
                  titleIcon={<CurrencyCircleDollar />}
                  value={formatNumber(cost) + " " + company?.currency}
                  noTruncate
                />
                {hasShipmentMethod && (
                  <InformationRow
                    title="Shipment Method"
                    titleIcon={
                      incomingOrders[0]?.shipmentMethod ? (
                        <ShipmentMethodIcon shipmentMethod={incomingOrders[0]?.shipmentMethod} />
                      ) : (
                        <Boat />
                      )
                    }
                    value={
                      hasShipmentMethod
                        ? formatUniqueValues(
                            uniq(
                              incomingOrders
                                .map((order) => startCase(lowerCase(order.shipmentMethod ?? "")))
                                .filter((method): method is string => Boolean(method)),
                            ),
                            "methods",
                          )
                        : "Unknown shipment method"
                    }
                  />
                )}
                {hasExternalCollectionId && (
                  <InformationRow
                    title="Collection"
                    titleIcon={<TShirt />}
                    value={
                      hasExternalCollectionId
                        ? formatUniqueValues(
                            uniq(
                              incomingOrders
                                .map((order) => order.externalCollectionId)
                                .filter((id): id is string => Boolean(id)),
                            ).map((x) => collections?.find((c) => c.externalId == x)?.name ?? x),
                            "collections",
                          )
                        : "No collection"
                    }
                  />
                )}
                {hasVendorId && (
                  <InformationRow
                    title="Vendor"
                    titleIcon={<Factory />}
                    value={formatUniqueValues(
                      uniq(
                        incomingOrders
                          .map((order) => order.vendorId)
                          .filter((id): id is string => Boolean(id)),
                      ).map((id) => vendors?.find((v) => v.vendorId === id)?.name ?? id),
                      "vendors",
                    )}
                  />
                )}
                {/* Extra data fields from incoming orders */}
                {extraDataFields.map((fieldName) => {
                  const data = extraDataValues.get(fieldName)
                  const title = getExtraDataDisplayName(
                    fieldName,
                    ExtraDataTable.INCOMING_ORDER_ITEM,
                    displayNameMap,
                  )
                  return (
                    <InformationRow
                      key={fieldName}
                      title={title}
                      titleIcon={<Tag />}
                      value={data?.formatted ?? "-"}
                      valueTooltip={
                        data && data.uniqueValues.length > 1 ? (
                          <UniqueValueListContent values={data.uniqueValues} title={title} />
                        ) : undefined
                      }
                      noTruncate
                    />
                  )
                })}
              </InformationGrid>
            </DrawerSection>
            <div className="border-t-[1px] border-base  w-full" />
            <DrawerSection className="flex flex-col gap-3 h-full">
              <div className="label-base">Order Items</div>
              <div className="w-full h-full">{variantsTable}</div>
              <div
                className="label-sm flex flex-row gap-1.5 items-center hover:underline cursor-pointer"
                onClick={() => setShowAllVariants(true)}
              >
                Show all variants
                <CaretRight className="text-fg-subtle" />
              </div>
            </DrawerSection>
          </>
        )}

        {showAllVariants && (
          <>
            <div className="border-t-[1px] border-base  w-full" />
            <DrawerSection className="flex flex-col gap-3 h-full">
              <div
                className="label-sm flex flex-row items-center gap-1.5 hover:underline cursor-pointer"
                onClick={() => setShowAllVariants(false)}
              >
                <CaretLeft className="text-fg-subtle" />
                All variants
              </div>
              <div className="w-full h-full">{variantsTable}</div>
            </DrawerSection>
          </>
        )}
      </DrawerBody>
    </>
  )
}

export default IncomingOrdersDrawer
